import {useCallback} from 'react';

import classNames from 'classnames';

import {colors} from 'shells/colors/colors';
import {EditableLabel} from 'shells/editable-label/editable-label';
import {HouseUserRegularIcon} from 'shells/icon';
import {LinkedEntity} from 'shells/linked-entity/linked-entity';
import {Body} from 'shells/typography/body';

import type {ContactPageFragment} from 'nutshell-graphql-types';

import {useUpdatePeepMap} from '../../graphql/hooks/use-update-peep-map';

import {contactRefetchForEvent} from '../hooks/use-contact-update-refetch';

interface Props {
    contact: ContactPageFragment;
}

export function ContactContextOverview(props: Props) {
    const {contact} = props;
    const {updatePeepMap} = useUpdatePeepMap();

    const hasPrimaryAccount = contact.primaryAccount !== null;
    const primaryAccount = contact.primaryAccount?.node;
    const primaryRelationshipNote = contact.primaryAccount?.relationshipNote;

    const handleSaveRelationshipNote = useCallback(
        (newValue: string) => {
            if (!primaryAccount) return Promise.resolve();

            return updatePeepMap({
                contact: contact.id,
                account: primaryAccount.id,
                relationship: newValue || '',
            }).then(() => {
                contactRefetchForEvent('contactAccountsUpdated');
            });
        },
        [contact.id, primaryAccount, updatePeepMap]
    );

    if (!hasPrimaryAccount) {
        return null;
    }

    return (
        <div
            className={classNames('flex items-center', {
                'py-4': NutClientConfig.hasCoreEntityFullHeightSidebarFF,
            })}
        >
            {/* @ts-expect-error */}
            <HouseUserRegularIcon size={20} alignCenter={true} fill={colors.blueDk} />
            <EditableLabel
                value={primaryRelationshipNote ?? ''}
                onSaveNewValue={handleSaveRelationshipNote}
                placeholder='Add role…'
                allowSavingEmpty={true}
                shouldAllowUndo={false}
                shouldCenterText={true}
                tooltip='Edit role'
                shouldTruncate={true}
            />
            <Body isBold={true}>
                <LinkedEntity
                    type='accounts'
                    name={`@${primaryAccount?.name}`}
                    htmlUrlPath={primaryAccount?.htmlUrlPath}
                    shouldClientRoute={true}
                    useEntityColor={false}
                    displayInline={true}
                    allowWrap={false}
                />
            </Body>
        </div>
    );
}
